import React from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Button } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const getStatusStyle = (status) => {
  const styles = {
    ACTIVE: { backgroundColor: "#198754", color: "#fff" },
    INACTIVE: { backgroundColor: "#6c757d", color: "#fff" },
    MAINTENANCE: { backgroundColor: "#ffc107", color: "#000" },
    RETIRED: { backgroundColor: "#343a40", color: "#fff" },
  };
  return styles[status] || styles.INACTIVE;
};

const getMaintenanceTypeStyle = (type) => {
  const styles = {
    SCHEDULED: { backgroundColor: "#0d6efd", color: "#fff" },
    REPAIR: { backgroundColor: "#ffc107", color: "#000" },
    INSPECTION: { backgroundColor: "#0dcaf0", color: "#000" },
    CLEANING: { backgroundColor: "#6c757d", color: "#fff" },
    REPLACEMENT: { backgroundColor: "#343a40", color: "#fff" },
    EMERGENCY: { backgroundColor: "#ff5454", color: "#fff" },
    OTHER: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[type] || styles.OTHER;
};

const getRecordStatusStyle = (status) => {
  const styles = {
    SCHEDULED: { backgroundColor: "#0dcaf0", color: "#000" },
    IN_PROGRESS: { backgroundColor: "#ffc107", color: "#000" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    CANCELLED: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[status] || styles.SCHEDULED;
};

const MachineDetail = ({
  machine,
  maintenanceHistory,
  loading,
  error,
  onStartMaintenance,
}) => {
  if (loading && !machine) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="alert alert-danger py-2">
        <i className="fa fa-exclamation-circle me-2"></i>
        Error loading machine: {error.message || "Unknown error"}
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="alert alert-warning py-2">
        Machine not found
      </div>
    );
  }

  return (
    <div className="machine-detail">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 className="mb-1">
            <i className="fa fa-cog me-2"></i>
            {machine.name}
          </h4>
          <small className="text-muted">
            {machine.machine_type_name} | {machine.location_name}
          </small>
        </div>
        <div>
          <Link to="/maintenance/machines" className="btn btn-outline-secondary btn-sm me-1">
            <i className="fa fa-arrow-left me-1"></i> Back
          </Link>
          <Link to={`/maintenance/machines/${machine.id}/edit`} className="btn btn-outline-primary btn-sm me-1">
            <i className="fa fa-pencil me-1"></i> Edit
          </Link>
          <Link
            to={`/maintenance/records/new?machine=${machine.id}`}
            className="btn btn-success btn-sm"
          >
            <i className="fa fa-plus me-1"></i> Schedule
          </Link>
        </div>
      </div>

      <Row>
        {/* Machine Info */}
        <Col md={8}>
          <Card className="mb-3">
            <Card.Header className="reports-filter">
              <i className="fa fa-info-circle me-2"></i>
              Machine Information
            </Card.Header>
            <Card.Body className="py-2">
              <Row>
                <Col md={6}>
                  <table className="table table-borderless table-sm mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: "40%" }}>Status</td>
                        <td>
                          <span className="badge badge-pill" style={getStatusStyle(machine.status)}>
                            {machine.status_display || machine.status}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Type</td>
                        <td>{machine.machine_type_name}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Location</td>
                        <td>{machine.location_name}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Serial Number</td>
                        <td>{machine.serial_number || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Model</td>
                        <td>{machine.model_number || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Manufacturer</td>
                        <td>{machine.manufacturer || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <table className="table table-borderless table-sm mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: "45%" }}>Purchase Date</td>
                        <td>
                          {machine.purchase_date
                            ? moment(machine.purchase_date).format("MMM D, YYYY")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Installation Date</td>
                        <td>
                          {machine.installation_date
                            ? moment(machine.installation_date).format("MMM D, YYYY")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Warranty</td>
                        <td>
                          {machine.warranty_expiration ? (
                            machine.is_under_warranty ? (
                              <span className="badge badge-pill" style={{ backgroundColor: "#198754" }}>
                                <i className="fa fa-shield me-1"></i>
                                {moment(machine.warranty_expiration).format("MMM D, YYYY")}
                              </span>
                            ) : (
                              <span className="badge badge-pill" style={{ backgroundColor: "#6c757d" }}>
                                Expired
                              </span>
                            )
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Maintenance Freq.</td>
                        <td>
                          {machine.maintenance_frequency_days
                            ? `Every ${machine.maintenance_frequency_days} days`
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Last Maintenance</td>
                        <td>
                          {machine.last_maintenance_date
                            ? moment(machine.last_maintenance_date).format("MMM D, YYYY")
                            : "Never"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Next Maintenance</td>
                        <td>
                          {machine.next_maintenance_date ? (
                            <>
                              {moment(machine.next_maintenance_date).format("MMM D, YYYY")}
                              {machine.is_maintenance_due && (
                                <span className="badge badge-pill ms-1" style={{ backgroundColor: "#ff5454" }}>
                                  Due
                                </span>
                              )}
                            </>
                          ) : (
                            "Not scheduled"
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>

              {machine.description && (
                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted d-block mb-1">Description</small>
                  <p className="mb-0 small">{machine.description}</p>
                </div>
              )}

              {machine.notes && (
                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted d-block mb-1">Notes</small>
                  <p className="mb-0 small">{machine.notes}</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Maintenance History */}
          <Card>
            <Card.Header className="reports-filter d-flex justify-content-between align-items-center">
              <span>
                <i className="fa fa-history me-2"></i>
                Maintenance History
              </span>
              <span className="badge badge-pill" style={{ backgroundColor: "#6c757d" }}>
                {machine.total_maintenance_records || maintenanceHistory.length || 0}
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              {(machine.maintenance_records || maintenanceHistory || []).length === 0 ? (
                <div className="shipment-list--empty text-center py-4">
                  <i className="fa fa-history fa-2x mb-2 text-muted"></i>
                  <p className="mb-0 text-muted">No maintenance history</p>
                </div>
              ) : (
                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Cost</th>
                      <th style={{ width: "60px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(machine.maintenance_records || maintenanceHistory || []).map((record) => (
                      <tr key={record.id}>
                        <td>
                          {record.completed_at
                            ? moment(record.completed_at).format("MMM D, YYYY")
                            : record.scheduled_date
                            ? moment(record.scheduled_date).format("MMM D, YYYY")
                            : "-"}
                        </td>
                        <td>
                          <span className="badge badge-pill" style={getMaintenanceTypeStyle(record.maintenance_type)}>
                            {record.maintenance_type_display || record.maintenance_type}
                          </span>
                        </td>
                        <td>
                          <Link to={`/maintenance/records/${record.id}`}>
                            {record.title}
                          </Link>
                          {record.service_provider_name && (
                            <small className="d-block text-muted">
                              by {record.service_provider_name}
                            </small>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-pill" style={getRecordStatusStyle(record.status)}>
                            {record.status_display || record.status}
                          </span>
                        </td>
                        <td>
                          {record.cost ? `$${parseFloat(record.cost).toLocaleString()}` : "-"}
                        </td>
                        <td className="text-center">
                          <Link
                            to={`/maintenance/records/${record.id}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="fa fa-eye"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col md={4}>
          {/* Photo */}
          {machine.photo && (
            <Card className="mb-3">
              <Card.Body className="p-2">
                <img
                  src={machine.photo}
                  alt={machine.name}
                  className="img-fluid rounded"
                  style={{ width: "100%" }}
                />
              </Card.Body>
            </Card>
          )}

          {/* Quick Stats */}
          <Card className="mb-3">
            <Card.Header className="reports-filter">
              <i className="fa fa-bar-chart me-2"></i>
              Quick Stats
            </Card.Header>
            <Card.Body className="py-2">
              <table className="table table-borderless table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted">Total Maintenance</td>
                    <td className="text-end">
                      <strong>{machine.total_maintenance_records || 0}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Days Until Maintenance</td>
                    <td className="text-end">
                      {machine.days_until_maintenance !== null ? (
                        <strong style={{ color: machine.days_until_maintenance < 0 ? "#ff5454" : "inherit" }}>
                          {machine.days_until_maintenance < 0
                            ? `${Math.abs(machine.days_until_maintenance)} overdue`
                            : machine.days_until_maintenance}
                        </strong>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Created By</td>
                    <td className="text-end">{machine.created_by_name || "-"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Created At</td>
                    <td className="text-end">
                      {moment(machine.created_at).format("MMM D, YYYY")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>

          {/* Recent Maintenance */}
          {machine.recent_maintenance && (
            <Card className="mb-3">
              <Card.Header className="reports-filter">
                <i className="fa fa-clock-o me-2"></i>
                Most Recent Maintenance
              </Card.Header>
              <Card.Body className="py-2">
                <Link to={`/maintenance/records/${machine.recent_maintenance.id}`}>
                  <strong>{machine.recent_maintenance.title}</strong>
                </Link>
                <div className="mt-1">
                  <span className="badge badge-pill" style={getMaintenanceTypeStyle(machine.recent_maintenance.maintenance_type)}>
                    {machine.recent_maintenance.maintenance_type}
                  </span>
                </div>
                <small className="text-muted d-block mt-1">
                  Completed {moment(machine.recent_maintenance.completed_at).format("MMM D, YYYY")}
                </small>
              </Card.Body>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <Card.Header className="reports-filter">
              <i className="fa fa-bolt me-2"></i>
              Quick Actions
            </Card.Header>
            <Card.Body className="py-2 d-grid gap-2">
              <Link
                to={`/maintenance/records/new?machine=${machine.id}`}
                className="btn btn-success btn-sm"
              >
                <i className="fa fa-calendar-plus-o me-1"></i> Schedule Maintenance
              </Link>
              <Link
                to={`/maintenance/records/service-visit?machine=${machine.id}`}
                className="btn btn-info btn-sm"
              >
                <i className="fa fa-clipboard me-1"></i> Record Service Visit
              </Link>
              {machine.status === "ACTIVE" && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => onStartMaintenance && onStartMaintenance(machine.id)}
                >
                  <i className="fa fa-wrench me-1"></i> Mark Under Maintenance
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MachineDetail;
